import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Play, Clock, BarChart3, Trash2, X, Check, User as UserIcon, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PageHeader, Button, Badge, EmptyState } from "@/components/ui-em";

// Extract a YouTube/Vimeo embed URL from any share URL. Returns null if not recognised.
function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    // youtu.be/<id>
    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      return id ? `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1&autoplay=1` : null;
    }
    // youtube.com/watch?v=<id>  |  /shorts/<id>  |  /embed/<id>
    if (u.hostname.includes("youtube.com")) {
      let id = u.searchParams.get("v");
      if (!id) {
        const m = u.pathname.match(/\/(?:shorts|embed)\/([^/?#]+)/);
        if (m) id = m[1];
      }
      return id ? `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1&autoplay=1` : null;
    }
    // vimeo.com/<id>
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      return id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}?autoplay=1` : null;
    }
    return null;
  } catch {
    return null;
  }
}

function VideoPlayerModal({ training, onClose }) {
  const embed = toEmbedUrl(training?.video_url);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!training) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="video-modal-backdrop"
    >
      <div
        className="w-full max-w-5xl em-card overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-white/5">
          <div className="min-w-0">
            <h3 className="font-display text-lg text-white truncate">{training.title}</h3>
            {training.instructor && (
              <p className="text-xs text-zinc-500 mt-0.5">{training.instructor}</p>
            )}
          </div>
          <button
            data-testid="close-video-modal"
            onClick={onClose}
            className="text-zinc-400 hover:text-white p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="aspect-video bg-black">
          {embed ? (
            <iframe
              data-testid="video-iframe"
              src={embed}
              title={training.title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center px-6">
              <p className="text-sm text-zinc-400">This training has no playable video attached yet.</p>
              {training.video_url && (
                <a
                  href={training.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gold hover:underline text-sm inline-flex items-center gap-1.5"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open source link
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DifficultyBar({ level }) {
  const total = 3;
  const active = level === "Beginner" ? 1 : level === "Intermediate" ? 2 : 3;
  return (
    <div className="flex gap-1">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`h-1 w-4 rounded-full ${i < active ? "bg-gold" : "bg-white/10"}`}
        />
      ))}
    </div>
  );
}

export default function Academy() {
  const [trainings, setTrainings] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", duration_min: 30, difficulty: "Beginner", video_url: "" });
  const [playing, setPlaying] = useState(null);
  const { user } = useAuth();
  const { t } = useTranslation();

  const load = () => {
    Promise.all([api.get("/academy/trainings"), api.get("/academy/my-progress")])
      .then(([tr, p]) => {
        setTrainings(tr.data);
        setProgress(p.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const createTraining = async (e) => {
    e.preventDefault();
    try {
      await api.post("/academy/trainings", form);
      toast.success(t("adminAcademy.trainingCreated"));
      setShowForm(false);
      setForm({ title: "", description: "", duration_min: 30, difficulty: "Beginner", video_url: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("adminAcademy.confirmDelete"))) return;
    try {
      await api.delete(`/academy/trainings/${id}`);
      toast.success(t("adminAcademy.trainingDeleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setProg = async (id, val) => {
    try {
      await api.post(`/academy/trainings/${id}/progress?progress=${val}`);
      setProgress((p) => ({ ...p, [id]: val }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openVideo = (tr) => {
    setPlaying(tr);
    const cur = progress[tr.id] || 0;
    if (cur < 10) setProg(tr.id, 10);
  };

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t("dashboard.module")}
        title={t("academy.title")}
        description={t("academy.description")}
        actions={
          user?.role === "admin" ? (
            <Button icon={Plus} onClick={() => setShowForm(true)} size="lg" data-testid="new-training-btn">{t("adminAcademy.newTraining")}</Button>
          ) : null
        }
      />

      {loading ? (
        <div className="text-zinc-500 text-sm">{t("common.loading")}</div>
      ) : trainings.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title={t("academy.noTrainings")}
          description={t("academy.description")}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {trainings.map((tr) => {
            const prog = progress[tr.id] || 0;
            const done = prog >= 100;
            return (
              <div key={tr.id} data-testid={`training-${tr.id}`} className="em-card em-card-hover overflow-hidden flex flex-col">
                <button
                  type="button"
                  onClick={() => openVideo(tr)}
                  data-testid={`play-${tr.id}`}
                  className="aspect-video relative bg-ink-0 border-b border-white/5 overflow-hidden group text-left"
                  aria-label={`Play ${tr.title}`}
                >
                  {(() => {
                    const url = tr.video_url || "";
                    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([^&?/#]+)/);
                    const ytId = m ? m[1] : null;
                    return ytId ? (
                      <img
                        src={`https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90"
                        style={{ transitionProperty: "opacity", transitionDuration: "220ms" }}
                      />
                    ) : (
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            "radial-gradient(circle at 30% 20%, rgba(212,175,55,0.15) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(212,175,55,0.08) 0%, transparent 50%)",
                        }}
                      />
                    );
                  })()}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-gold/90 border border-gold flex items-center justify-center shadow-lg group-hover:scale-110" style={{ transitionProperty: "transform", transitionDuration: "220ms" }}>
                      <Play className="w-6 h-6 text-ink-0 ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                  <div className="absolute top-3 left-3 flex items-center gap-1.5">
                    <Badge variant="default" size="sm" icon={Clock}>{tr.duration_min}m</Badge>
                    {tr.category && tr.category !== "General" && (
                      <Badge variant="gold" size="sm">{tr.category}</Badge>
                    )}
                  </div>
                  {done && (
                    <div className="absolute top-3 right-3">
                      <Badge variant="success" size="sm" icon={Check} uppercase>{t("academy.completed")}</Badge>
                    </div>
                  )}
                </button>
                <div className="p-6 flex-1 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <DifficultyBar level={tr.difficulty} />
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{t(`academy.${tr.difficulty.toLowerCase()}`, tr.difficulty)}</span>
                  </div>
                  <h3 className="font-display text-lg font-medium text-white">{tr.title}</h3>
                  <p className="text-sm text-zinc-400 line-clamp-2 flex-1">{tr.description}</p>
                  {tr.instructor && (
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <UserIcon className="w-3 h-3" strokeWidth={1.75} />
                      <span>{tr.instructor}</span>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center justify-between text-xs text-zinc-500 mb-1.5">
                      <span>{t("academy.myProgress")}</span>
                      <span>{prog}%</span>
                    </div>
                    <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-gold" style={{ width: `${prog}%`, transition: "width 400ms ease" }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <button
                      onClick={() => openVideo(tr)}
                      data-testid={`watch-${tr.id}`}
                      className="flex-1 bg-gold hover:bg-gold-hover text-ink-0 text-sm font-semibold rounded-lg px-3 py-2 inline-flex items-center justify-center gap-1.5"
                      style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
                    >
                      <Play className="w-3.5 h-3.5" fill="currentColor" />
                      {prog === 0 ? t("academy.watchNow") : done ? t("academy.completed") : t("dashboard.continueLearning")}
                    </button>
                    <button
                      onClick={() => setProg(tr.id, Math.min(100, prog + 25))}
                      data-testid={`progress-${tr.id}`}
                      className="bg-white/5 hover:bg-white/10 text-white text-sm rounded-lg px-3 py-2"
                      style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
                      title={t("academy.myProgress")}
                    >
                      +25%
                    </button>
                    {user?.role === "admin" && (
                      <button
                        onClick={() => remove(tr.id)}
                        className="p-2 text-zinc-500 hover:text-red-300"
                        style={{ transitionProperty: "color", transitionDuration: "160ms" }}
                        title={t("common.delete")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {playing && (
        <VideoPlayerModal training={playing} onClose={() => setPlaying(null)} />
      )}

      {showForm && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="em-card p-8 w-full max-w-lg">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-xl">{t("adminAcademy.newTraining")}</h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={createTraining} className="space-y-4">
              <input data-testid="training-title" required placeholder={t("common.title")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="em-input" />
              <textarea data-testid="training-desc" required placeholder={t("common.description")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="em-input" rows={3} />
              <div className="grid grid-cols-2 gap-3">
                <input type="number" min={1} placeholder={t("common.duration") + " (min)"} value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value) || 0 })} className="em-input" />
                <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="em-input">
                  <option value="Beginner">{t("academy.beginner")}</option>
                  <option value="Intermediate">{t("academy.intermediate")}</option>
                  <option value="Advanced">{t("academy.advanced")}</option>
                </select>
              </div>
              <input placeholder="Video URL" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className="em-input" />
              <button data-testid="training-submit" type="submit" className="w-full bg-gold text-ink-0 font-semibold rounded-lg px-5 py-2.5 hover:bg-gold-hover" style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}>
                {t("common.save")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
